// static/table_engine_src/plugins/sigmaBucketsPopover.js
// Generic sigma-buckets popover (tiny bar chart) for TableEngine tables.
// Styled via UiPopovers (ui_popovers.css / ui_popovers.js).
//
// UPDATE (2026-01-17):
//  - Works for TOTAL row (tfoot).
//  - Header ring marker can be O(1): page may set table.__teSigmaFilledCols = { vega:true }.
//    If present, we do NOT scan rows to decide whether to show the ring.
//  - UX polish: show "clickable" affordance only when the trigger column is filled.
//  - NEW: hover overlay covers the number (does not break alignment) + ring kept.
//
// ---------------------------------------------------------
// Internal state (single popover reused across tables)
// ---------------------------------------------------------
const sigmaPop = {
  pop: null,
  el: null,
  canvas: null,
  subEl: null,
  summaryEl: null,
  tipEl: null,
  btnCsv: null,
  btnJson: null,

  lastAnchor: null,
  lastTr: null,
  lastVectorKey: null,
  lastColKey: null,
};

// ---------------------------------------------------------
// Small helpers
// ---------------------------------------------------------
function clamp(x, a, b) {
  const v = Number(x);
  if (!Number.isFinite(v)) return a;
  return Math.min(b, Math.max(a, v));
}

function safeNum(x, fallback = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : fallback;
}

function cssEsc(s) {
  try {
    return CSS.escape(String(s));
  } catch {
    return String(s).replace(/["\\]/g, "\\$&");
  }
}

function fmtFixed(x, dp = 6) {
  const n = Number(x);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(dp).replace(/\.?0+$/, "");
}

function isTotalRow(tr) {
  if (!tr) return false;
  if (tr.closest?.("tfoot")) return true;
  if (tr.classList?.contains("te-total-row")) return true;
  if (tr.classList?.contains("te-total")) return true;
  if (tr.dataset?.teTotal === "1") return true;
  return false;
}

function buildSummary(vec) {
  try {
    let sum = 0;
    let maxAbs = 0;
    for (let i = 0; i < vec.length; i++) {
      const v = Number(vec[i]);
      if (!Number.isFinite(v)) continue;
      sum += v;
      const av = Math.abs(v);
      if (av > maxAbs) maxAbs = av;
    }

    const fmt = (x) => {
      const ax = Math.abs(x);
      if (ax >= 1000) return x.toFixed(0);
      if (ax >= 100) return x.toFixed(2);
      if (ax >= 1) return x.toFixed(6);
      return x.toFixed(8);
    };

    return `Σ ${fmt(sum)}   •   max|.| ${fmt(maxAbs)}`;
  } catch {
    return "";
  }
}

function getLabels(n, opts) {
  const fromOpts = opts && Array.isArray(opts.labels) ? opts.labels : null;
  if (fromOpts && fromOpts.length >= n) return fromOpts.slice(0, n);

  const labels =
    (typeof window !== "undefined" && (window.__sigmaBucketLabels ||
      window.__sigmaLabels ||
      window.__scenarioSigmaBucketLabels ||
      window.__scenarioSigmaLabels)) ||
    null;

  if (Array.isArray(labels) && labels.length >= n) return labels.slice(0, n);
  return Array.from({ length: n }, (_, i) => String(i));
}

// Diverging palette (blue -> neutral -> orange)
function colorDiverging(t) {
  const x = clamp(t, 0, 1);

  const c0 = [73, 141, 255];
  const c1 = [230, 240, 255];
  const c2 = [255, 168, 89];

  let a, b, u;
  if (x <= 0.5) {
    a = c0; b = c1; u = x / 0.5;
  } else {
    a = c1; b = c2; u = (x - 0.5) / 0.5;
  }

  const r = Math.round(a[0] + (b[0] - a[0]) * u);
  const g = Math.round(a[1] + (b[1] - a[1]) * u);
  const bb = Math.round(a[2] + (b[2] - a[2]) * u);

  return `rgb(${r},${g},${bb})`;
}

// ---------------------------------------------------------
// Clipboard + command icons
// ---------------------------------------------------------
async function copyTextToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(text ?? ""));
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement("textarea");
    ta.value = String(text ?? "");
    ta.setAttribute("readonly", "true");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.top = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}

function svgIcon(name) {
  if (name === "copy") {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
      </svg>
    `.trim();
  }
  if (name === "code") {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M16 18l6-6-6-6"></path>
        <path d="M8 6l-6 6 6 6"></path>
        <path d="M14 4l-4 16"></path>
      </svg>
    `.trim();
  }
  if (name === "check") {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 6L9 17l-5-5"></path>
      </svg>
    `.trim();
  }
  if (name === "x") {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 6L6 18"></path>
        <path d="M6 6l12 12"></path>
      </svg>
    `.trim();
  }
  return "";
}

function flashIcon(btn, ok) {
  if (!btn) return;
  if (!btn.__teOrigIcon) btn.__teOrigIcon = btn.innerHTML;

  btn.innerHTML = ok ? svgIcon("check") : svgIcon("x");
  btn.disabled = true;
  btn.style.opacity = "0.85";

  clearTimeout(btn.__teFlashT);
  btn.__teFlashT = setTimeout(() => {
    btn.innerHTML = btn.__teOrigIcon;
    btn.disabled = false;
    btn.style.opacity = "";
  }, ok ? 850 : 1200);
}

// ---------------------------------------------------------
// Header ring marker + "hover overlay" affordance
// ---------------------------------------------------------
function ensureColLabelWrapper(th) {
  if (!th) return;
  if (th.querySelector(":scope > .te-col-label")) return;

  const wrap = document.createElement("span");
  wrap.className = "te-col-label";
  while (th.firstChild) wrap.appendChild(th.firstChild);
  th.appendChild(wrap);
}

function clearHeaderMarks(table) {
  const thead = table?.querySelector("thead");
  if (!thead) return;
  thead.querySelectorAll('th[data-te-has-popover-col="1"]').forEach((th) => {
    delete th.dataset.teHasPopoverCol;
  });
}

function defaultVectorKeyForCol(colKey) {
  if (colKey === "vega") return "sigma_sens";
  return colKey;
}

function allDataRowsForScan(table) {
  if (!table) return [];
  const rows = [];
  rows.push(...Array.from(table.querySelectorAll("tbody tr")));
  rows.push(...Array.from(table.querySelectorAll("tfoot tr")));
  return rows;
}

function readFilledColsFlag(table) {
  const v = table?.__teSigmaFilledCols;
  if (!v) return null;

  if (v instanceof Set) return (colKey) => v.has(String(colKey));
  if (typeof v === "object") return (colKey) => !!v[String(colKey)];
  return null;
}

function ensureAffordanceStyles() {
  if (document.getElementById("__te_sigma_afford_styles")) return;

  const st = document.createElement("style");
  st.id = "__te_sigma_afford_styles";
  st.textContent = `
    /* Only applies when table has __teSigmaAfford and the cell has te-sigma-clickable */
    table.__teSigmaAfford td.te-sigma-clickable{
      cursor: pointer;
      position: relative; /* for overlay */
    }

    /* Hover overlay that COVERS the number without shifting layout */
    table.__teSigmaAfford td.te-sigma-clickable::after{
      content: "⋯";
      position: absolute;
      inset: 2px;                /* small inner padding to avoid borders */
      display: flex;
      align-items: center;
      justify-content: center;

      /* The "cover" */
      background: rgba(10, 18, 32, 0.72);
      border: 1px solid rgba(255,255,255,0.10);
      border-radius: 12px;
      box-shadow: 0 10px 18px rgba(0,0,0,0.22);

      /* The dots */
      color: rgba(220, 235, 255, 0.92);
      font-size: 18px;
      letter-spacing: 2px;
      line-height: 1;

      opacity: 0;
      pointer-events: none;      /* click still goes to td */
      transition: opacity 120ms ease;
    }

    table.__teSigmaAfford td.te-sigma-clickable:hover::after,
    table.__teSigmaAfford td.te-sigma-clickable:focus-within::after{
      opacity: 1;
    }

    /* optional: subtle background tint on hover (very mild) */
    table.__teSigmaAfford td.te-sigma-clickable:hover{
      background: rgba(90, 160, 255, 0.06);
    }
  `;
  document.head.appendChild(st);
}

function setClickableAffordance(table, cfg, filledFn) {
  if (!table) return;

  ensureAffordanceStyles();

  const trigger =
    Array.isArray(cfg?.trigger) && cfg.trigger.length
      ? cfg.trigger.map(String)
      : ["sigma_sens", "vega"];

  const clickableCols = trigger.filter((k) => (filledFn ? filledFn(k) : false));

  if (clickableCols.length) table.classList.add("__teSigmaAfford");
  else table.classList.remove("__teSigmaAfford");

  table
    .querySelectorAll("tbody td.te-sigma-clickable, tfoot td.te-sigma-clickable")
    .forEach((td) => td.classList.remove("te-sigma-clickable"));

  for (const colKey of clickableCols) {
    const sel = `tbody td[data-col-key="${cssEsc(colKey)}"], tfoot td[data-col-key="${cssEsc(colKey)}"]`;
    table.querySelectorAll(sel).forEach((td) => td.classList.add("te-sigma-clickable"));
  }
}

// Keep ring + update affordance AFTER data exists
function refreshHeaderMarks(table, cfg) {
  if (!table) return;

  const trigger =
    Array.isArray(cfg?.trigger) && cfg.trigger.length
      ? cfg.trigger.map(String)
      : ["sigma_sens", "vega"];

  clearHeaderMarks(table);

  const thead = table.querySelector("thead");
  if (!thead) return;

  const vmap =
    cfg?.vectorKeyByCol && typeof cfg.vectorKeyByCol === "object"
      ? cfg.vectorKeyByCol
      : null;

  const hasFilled = readFilledColsFlag(table);
  if (hasFilled) {
    // ✅ O(1) ring + affordance
    for (const colKey of trigger) {
      if (!hasFilled(colKey)) continue;

      const th = thead.querySelector(`th[data-col-key="${cssEsc(colKey)}"]`);
      if (!th) continue;

      th.dataset.teHasPopoverCol = "1"; // <-- keep the ring
      ensureColLabelWrapper(th);
    }
    setClickableAffordance(table, cfg, hasFilled);
    return;
  }

  // Fallback: scan rows
  const rows = allDataRowsForScan(table);
  if (!rows.length) {
    setClickableAffordance(table, cfg, () => false);
    return;
  }

  const filled = Object.create(null);

  for (const colKey of trigger) {
    const vectorKey = String((vmap && vmap[colKey]) || defaultVectorKeyForCol(colKey));

    let hasAny = false;
    for (const tr of rows) {
      const vec = getVectorFromTr(tr, vectorKey, cfg);
      if (Array.isArray(vec) && vec.length) {
        hasAny = true;
        break;
      }
    }
    filled[colKey] = !!hasAny;
    if (!hasAny) continue;

    const th = thead.querySelector(`th[data-col-key="${cssEsc(colKey)}"]`);
    if (!th) continue;

    th.dataset.teHasPopoverCol = "1"; // <-- keep the ring
    ensureColLabelWrapper(th);
  }

  setClickableAffordance(table, cfg, (k) => !!filled[String(k)]);
}

// ---------------------------------------------------------
// Tooltip (hover values on bars)
// ---------------------------------------------------------
function ensureTipEl() {
  if (sigmaPop.tipEl) return sigmaPop.tipEl;

  const tip = document.createElement("div");
  tip.className = "ui-sigma-tip";
  tip.style.position = "fixed";
  tip.style.display = "none";
  tip.style.zIndex = "100000";
  tip.style.padding = "6px 8px";
  tip.style.borderRadius = "10px";
  tip.style.border = "1px solid rgba(255,255,255,0.16)";
  tip.style.background = "rgba(10, 18, 32, 0.96)";
  tip.style.boxShadow = "0 10px 24px rgba(0,0,0,0.35)";
  tip.style.color = "rgba(255,255,255,0.92)";
  tip.style.font = "12px ui-monospace, Menlo, Monaco, Consolas, 'Courier New', monospace";
  tip.style.pointerEvents = "none";
  document.body.appendChild(tip);

  sigmaPop.tipEl = tip;
  return tip;
}

function showChartTip(text, clientX, clientY) {
  const tip = ensureTipEl();
  tip.textContent = String(text || "");

  tip.style.display = "block";
  tip.style.visibility = "hidden";

  const pad = 10;
  const w = tip.offsetWidth || 180;
  const h = tip.offsetHeight || 32;

  const x = clamp(clientX + 14, pad, window.innerWidth - pad - w);
  const y = clamp(clientY + 14, pad, window.innerHeight - pad - h);

  tip.style.left = `${Math.round(x)}px`;
  tip.style.top = `${Math.round(y)}px`;
  tip.style.visibility = "visible";
}

function hideChartTip() {
  const tip = sigmaPop.tipEl;
  if (!tip) return;
  tip.style.display = "none";
}

// ---------------------------------------------------------
// CSS for chart area
// ---------------------------------------------------------
function ensureSigmaStyles() {
  if (document.getElementById("__te_sigma_popover_styles")) return;

  const st = document.createElement("style");
  st.id = "__te_sigma_popover_styles";
  st.textContent = `
    .ui-sigma-subline{
      margin: 2px 0 8px;
      font-size: 12px;
      color: var(--ui-muted, rgba(200,225,255,0.80));
      opacity: 0.95;
    }
    .ui-sigma-summary{
      margin: 0 0 10px;
      font-size: 12px;
      color: rgba(220,235,255,0.82);
      opacity: 0.95;
    }
    .ui-sigma-canvas{
      width: 520px;
      max-width: 100%;
      height: 180px;
      display: block;
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,0.10);
      background: rgba(255,255,255,0.03);
      box-shadow: 0 10px 24px rgba(0,0,0,0.18) inset;
    }
    .ui-sigma-foot{
      margin-top: 10px;
      font-size: 12px;
      color: rgba(220,235,255,0.78);
      opacity: 0.95;
    }
  `;
  document.head.appendChild(st);
}

// ---------------------------------------------------------
// Popover creation (UiPopovers)
// ---------------------------------------------------------
function ensureSigmaPopover() {
  if (sigmaPop.pop) return sigmaPop.pop;

  ensureSigmaStyles();

  if (!window.UiPopovers?.create) {
    console.error(
      "TableEngineSigmaBucketsPopover: UiPopovers not available. " +
        "Make sure ui_popovers.js is loaded."
    );
    return null;
  }

  const bodyHTML = `
    <div class="ui-sigma-subline" data-ui-sigma-sub="1"></div>
    <div class="ui-sigma-summary" data-ui-sigma-summary="1"></div>

    <canvas class="ui-sigma-canvas" width="520" height="180"></canvas>
    <div class="ui-sigma-foot">Tip: hover bars to see exact values. Click outside to close.</div>

    <div class="ui-cmdbar" data-ui-sigma-cmdbar="1" style="padding-top:12px;">
      <button type="button" class="ui-cmd-btn" data-ui-sigma-csv="1"
              title="Copy bucket values as CSV" aria-label="Copy CSV">
        ${svgIcon("copy")}
      </button>
      <button type="button" class="ui-cmd-btn" data-ui-sigma-json="1"
              title="Copy bucket values as JSON" aria-label="Copy JSON">
        ${svgIcon("code")}
      </button>
    </div>
  `.trim();

  const pop = window.UiPopovers.create({
    title: "σ buckets (dP/dσ)",
    bodyHTML,
    className: "ui-sigma-pop",
  });

  sigmaPop.pop = pop;
  sigmaPop.el = pop.el;
  sigmaPop.subEl = pop.el.querySelector('[data-ui-sigma-sub="1"]') || null;
  sigmaPop.summaryEl = pop.el.querySelector('[data-ui-sigma-summary="1"]') || null;
  sigmaPop.canvas = pop.el.querySelector("canvas.ui-sigma-canvas") || null;
  sigmaPop.btnCsv = pop.el.querySelector('[data-ui-sigma-csv="1"]') || null;
  sigmaPop.btnJson = pop.el.querySelector('[data-ui-sigma-json="1"]') || null;

  if (sigmaPop.canvas) installSigmaBarHover(sigmaPop.canvas);

  const baseClose = pop.close;
  pop.close = () => {
    baseClose();
    hideChartTip();
    sigmaPop.lastAnchor = null;
    sigmaPop.lastTr = null;
    sigmaPop.lastVectorKey = null;
    sigmaPop.lastColKey = null;
  };

  return pop;
}

function setPopoverTitle(pop, title) {
  const t = pop?.el?.querySelector?.(".ui-pop-title");
  if (t) t.textContent = String(title || "σ buckets (dP/dσ)");
}

function setPopoverSubtitle(text) {
  if (!sigmaPop.subEl) return;
  sigmaPop.subEl.textContent = String(text || "");
  sigmaPop.subEl.style.display = text ? "block" : "none";
}

function setPopoverSummary(text, show) {
  if (!sigmaPop.summaryEl) return;
  sigmaPop.summaryEl.textContent = String(text || "");
  sigmaPop.summaryEl.style.display = show && text ? "block" : "none";
}

function setCmdbarVisibility(opts) {
  const cmdbar = sigmaPop.el?.querySelector?.('[data-ui-sigma-cmdbar="1"]');
  if (!cmdbar) return;

  const cmds = opts?.commands;
  const enabled =
    cmds === true ||
    (Array.isArray(cmds) && cmds.length) ||
    opts?.copyCsv === true ||
    opts?.copyJson === true;

  cmdbar.style.display = enabled ? "flex" : "none";

  const wantCsv =
    opts?.copyCsv === true ||
    cmds === true ||
    (Array.isArray(cmds) && cmds.map(String).includes("csv"));
  const wantJson =
    opts?.copyJson === true ||
    cmds === true ||
    (Array.isArray(cmds) && cmds.map(String).includes("json"));

  if (sigmaPop.btnCsv) sigmaPop.btnCsv.style.display = wantCsv ? "" : "none";
  if (sigmaPop.btnJson) sigmaPop.btnJson.style.display = wantJson ? "" : "none";
}

// ---------------------------------------------------------
// Drawing
// ---------------------------------------------------------
function drawTinySigmaBars(canvas, values, labels, opts) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "rgba(0,0,0,0)";
  ctx.fillRect(0, 0, W, H);

  const valsRaw = Array.isArray(values) ? values.map((x) => Number(x)) : [];
  const vals = valsRaw.filter((x) => Number.isFinite(x));
  const n = vals.length;

  if (!n) {
    ctx.fillStyle = "rgba(235,245,255,0.92)";
    ctx.font = "14px system-ui, -apple-system, Segoe UI, Roboto, Arial";
    ctx.fillText("No sigma buckets in this row.", 18, 36);
    canvas.__sigmaBarsState = null;
    return;
  }

  const mL = 38, mR = 18, mT = 18, mB = 46;
  const plotW = W - mL - mR;
  const plotH = H - mT - mB;

  const maxAbs = Math.max(...vals.map((v) => Math.abs(v))) || 1.0;

  ctx.strokeStyle = "rgba(255,255,255,0.18)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(mL, mT);
  ctx.lineTo(mL, mT + plotH);
  ctx.lineTo(mL + plotW, mT + plotH);
  ctx.stroke();

  const y0 = mT + plotH / 2;
  ctx.strokeStyle = "rgba(255,255,255,0.14)";
  ctx.beginPath();
  ctx.moveTo(mL, y0);
  ctx.lineTo(mL + plotW, y0);
  ctx.stroke();

  const barW = plotW / n;
  const padX = Math.max(1, barW * 0.16);

  for (let i = 0; i < n; i++) {
    const v = vals[i];
    const a = Math.min(1, Math.abs(v) / maxAbs);
    const h = a * (plotH / 2 - 6);

    const x = mL + i * barW + padX / 2;
    const w = Math.max(1, barW - padX);

    const y = v >= 0 ? y0 - h : y0;
    const signed = v >= 0 ? a : -a;
    const v01 = (signed + 1) * 0.5;

    ctx.fillStyle = colorDiverging(v01);
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = "rgba(0,0,0,0.28)";
    ctx.strokeRect(x, y, w, h);
  }

  const lab =
    Array.isArray(labels) && labels.length === n
      ? labels
      : Array.from({ length: n }, (_, i) => String(i));

  ctx.fillStyle = "rgba(255,255,255,0.75)";
  ctx.font = "11px system-ui, -apple-system, Segoe UI, Roboto, Arial";
  ctx.textAlign = "center";

  const every = n > 24 ? 4 : n > 16 ? 2 : 1;
  for (let i = 0; i < n; i += every) {
    const xx = mL + i * barW + barW / 2;
    ctx.fillText(String(lab[i]), xx, mT + plotH + 18);
  }

  canvas.__sigmaBarsState = {
    values: vals,
    labels: lab,
    geom: { mL, mT, plotW, plotH, barW, y0, maxAbs },
    dp: safeNum(opts?.valueDp, 6),
  };
}

function installSigmaBarHover(canvas) {
  if (!canvas || canvas.__sigmaBarHoverInstalled) return;
  canvas.__sigmaBarHoverInstalled = true;

  const onMove = (e) => {
    const st = canvas.__sigmaBarsState;
    if (!st || !st.geom) return;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const mx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const my = (e.clientY - rect.top) * (canvas.height / rect.height);

    const { mL, mT, plotW, plotH, barW } = st.geom;

    if (mx < mL || mx > mL + plotW || my < mT || my > mT + plotH) {
      hideChartTip();
      return;
    }

    const i = Math.floor((mx - mL) / barW);
    if (!Number.isFinite(i) || i < 0 || i >= st.values.length) {
      hideChartTip();
      return;
    }

    const lab = st.labels?.[i] ?? String(i);
    const v = st.values[i];
    const dp = Number.isFinite(st.dp) ? st.dp : 6;
    showChartTip(`σ[${lab}] = ${fmtFixed(v, dp)}`, e.clientX, e.clientY);
  };

  canvas.addEventListener("mousemove", onMove, { passive: true });
  canvas.addEventListener("mouseleave", () => hideChartTip(), { passive: true });
}

// ---------------------------------------------------------
// Vector extraction + subtitle/meta
// ---------------------------------------------------------
function getVectorFromTr(tr, vectorKey, opts) {
  if (!tr) return null;

  if (typeof opts?.getVector === "function") {
    const v = opts.getVector(tr, vectorKey);
    if (Array.isArray(v)) return v;
  }

  if (vectorKey === "sigma_sens" && Array.isArray(tr.__sigmaSens)) return tr.__sigmaSens;
  if (Array.isArray(tr.__sigmaSens) && vectorKey == null) return tr.__sigmaSens;

  if (Array.isArray(tr.__sigma_sens)) return tr.__sigma_sens;

  const m = tr.__bucketVectors;
  if (m && vectorKey && Array.isArray(m[vectorKey])) return m[vectorKey];
  if (m && vectorKey === "sigma_sens" && Array.isArray(m.sigma_sens)) return m.sigma_sens;

  return null;
}

function getSubtitleFromTr(tr, opts) {
  if (!tr) return "";

  if (typeof opts?.getSubtitle === "function") {
    const s = opts.getSubtitle(tr);
    return s == null ? "" : String(s);
  }

  const meta = tr.__sigmaMeta || tr.__scenarioMeta || tr.__riskMeta || tr.__meta || null;

  if (meta && typeof meta === "object") {
    const id = String(meta.identifier || meta.id || "").trim();
    const scen = Number.isFinite(Number(meta.scenario_idx)) ? Number(meta.scenario_idx) : null;
    const shift = Number.isFinite(Number(meta.rate_level_pct)) ? Number(meta.rate_level_pct) : null;

    const parts = [];
    if (id) parts.push(`Bond: ${id}`);
    if (scen != null) parts.push(`Scen: ${scen}`);
    if (shift != null) parts.push(`Shift: ${shift.toFixed(2)}%`);
    return parts.join("  •  ");
  }

  if (isTotalRow(tr)) return "Total";
  return "";
}

function defaultTitleFor(vectorKey, clickedColKey) {
  if (clickedColKey === "vega") return "Vega (σ buckets)";
  if (vectorKey === "sigma_sens") return "σ buckets (dP/dσ)";
  return `${vectorKey} buckets`;
}

function openSigmaPopover(anchorEl, tr, vectorKey, clickedColKey, opts) {
  const vec = getVectorFromTr(tr, vectorKey, opts);
  if (!Array.isArray(vec) || !vec.length) return;

  const pop = ensureSigmaPopover();
  if (!pop || !sigmaPop.canvas) return;

  const title = (opts && opts.title) || defaultTitleFor(vectorKey, clickedColKey);
  setPopoverTitle(pop, title);
  setPopoverSubtitle(getSubtitleFromTr(tr, opts));

  const labels = getLabels(vec.length, opts);
  drawTinySigmaBars(sigmaPop.canvas, vec, labels, opts);

  const wantSummary = opts?.summary === true || opts?.showSummary === true;
  setPopoverSummary(buildSummary(vec), !!wantSummary);

  setCmdbarVisibility(opts);

  if (sigmaPop.btnCsv) {
    sigmaPop.btnCsv.onclick = async () => {
      const rows = ["bucket,label,value"];
      for (let i = 0; i < vec.length; i++) {
        const lab = labels?.[i] ?? String(i);
        const v = Number(vec[i]);
        const vv = Number.isFinite(v) ? String(v) : "";
        const sLab = String(lab);
        const qlab =
          sLab.includes(",") || sLab.includes('"') ? `"${sLab.replace(/"/g, '""')}"` : sLab;
        rows.push(`${i},${qlab},${vv}`);
      }
      const ok = await copyTextToClipboard(rows.join("\n"));
      flashIcon(sigmaPop.btnCsv, ok);
    };
  }

  if (sigmaPop.btnJson) {
    sigmaPop.btnJson.onclick = async () => {
      const payload = { vectorKey, labels, values: vec };
      const ok = await copyTextToClipboard(JSON.stringify(payload, null, 2));
      flashIcon(sigmaPop.btnJson, ok);
    };
  }

  sigmaPop.lastAnchor = anchorEl || null;
  sigmaPop.lastTr = tr || null;
  sigmaPop.lastVectorKey = vectorKey || null;
  sigmaPop.lastColKey = clickedColKey || null;

  pop.open(anchorEl || null);
  try {
    requestAnimationFrame(() => pop.position?.());
  } catch {}
}

function closeSigmaPopover() {
  if (sigmaPop.pop) sigmaPop.pop.close();
  else {
    hideChartTip();
    sigmaPop.lastAnchor = null;
    sigmaPop.lastTr = null;
    sigmaPop.lastVectorKey = null;
    sigmaPop.lastColKey = null;
  }
}

// ---------------------------------------------------------
// Install on a table
// ---------------------------------------------------------
function install(tableId, opts = {}) {
  const table = document.getElementById(tableId);
  if (!table || table.__teSigmaBucketsInstalled) return;
  table.__teSigmaBucketsInstalled = true;

  table.__teSigmaCfg = {
    trigger:
      Array.isArray(opts.trigger) && opts.trigger.length
        ? opts.trigger.map(String)
        : ["sigma_sens", "vega"],
    vectorKeyByCol: (opts.vectorKeyByCol && typeof opts.vectorKeyByCol === "object") ? opts.vectorKeyByCol : null,
    getVector: typeof opts.getVector === "function" ? opts.getVector : null,
    getSubtitle: typeof opts.getSubtitle === "function" ? opts.getSubtitle : null,
    labels: Array.isArray(opts.labels) ? opts.labels : null,
    title: opts.title,
    valueDp: opts.valueDp,
    commands: opts.commands,
    copyCsv: opts.copyCsv,
    copyJson: opts.copyJson,
    summary: opts.summary,
    showSummary: opts.showSummary,
  };

  refreshHeaderMarks(table, table.__teSigmaCfg);
  table.addEventListener("te:render", () => refreshHeaderMarks(table, table.__teSigmaCfg));

  table.addEventListener(
    "click",
    (e) => {
      const target = e.target;
      if (!target) return;

      const td = target.closest?.("tbody td, tfoot td");
      if (!td) return;

      const colKey = String(td.dataset?.colKey || td.getAttribute?.("data-col-key") || "");
      if (!colKey) return;

      const cfg = table.__teSigmaCfg || {};
      const trigger = Array.isArray(cfg.trigger) ? cfg.trigger : ["sigma_sens", "vega"];

      const hitByColKey = trigger.includes(colKey);
      const hitByDataOut = trigger.some((k) => !!target.closest?.(`span[data-out="${cssEsc(k)}"]`));
      if (!hitByColKey && !hitByDataOut) return;

      const tr = td.closest?.("tr");
      if (!tr) return;

      const vmap = cfg.vectorKeyByCol;
      const vectorKey = String((vmap && vmap[colKey]) || defaultVectorKeyForCol(colKey));

      const vec = getVectorFromTr(tr, vectorKey, cfg);
      if (!Array.isArray(vec) || !vec.length) return;

      if (
        sigmaPop.pop?.isOpen?.() &&
        sigmaPop.lastAnchor === td &&
        sigmaPop.lastVectorKey === vectorKey
      ) {
        closeSigmaPopover();
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      openSigmaPopover(td, tr, vectorKey, colKey, cfg);
      e.preventDefault();
      e.stopPropagation();
    },
    true
  );
}

function refresh(tableId) {
  const table = document.getElementById(tableId);
  if (!table) return;
  const cfg = table.__teSigmaCfg || null;
  if (!cfg) return;
  refreshHeaderMarks(table, cfg);
}

// ---------------------------------------------------------
// Public API (export + global)
// ---------------------------------------------------------
export const TableEngineSigmaBucketsPopover = Object.freeze({
  install,
  refresh,
  close: closeSigmaPopover,
});
